import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import {
  Alert, Button, CircularProgress, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, InputAdornment, OutlinedInput, Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import AppDialog from '../../../shared/components/AppDialog';
import LabeledField from '../../../shared/components/LabeledField';
import { applyApiFieldErrors } from '../../../shared/api';
import {
  isValidOutsourcingAmount,
  toOutsourcingForm,
  toOutsourcingPayload,
} from '../model/outsourcingForm';

export default function OutsourcingDialog({ open, source, year, saving, onClose, onSubmit }) {
  const { t } = useTranslation();
  const isExisting = source?.calculationStatus !== 'EMPTY' || Number(source?.amountJpy) > 0;
  const {
    control,
    handleSubmit,
    reset,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useForm({
    mode: 'onTouched',
    defaultValues: toOutsourcingForm(source),
  });

  useEffect(() => {
    if (open) reset(toOutsourcingForm(source));
  }, [open, reset, source]);

  const noteLength = watch('note')?.length || 0;
  const submit = handleSubmit(async (values) => {
    clearErrors();
    try {
      await onSubmit(toOutsourcingPayload(values));
    } catch (error) {
      if (!applyApiFieldErrors(error, setError, t, {
        mapField: (field) => {
          const name = String(field || '').split('.').pop();
          return ['amount_jpy', 'note'].includes(name) ? name : null;
        },
      })) throw error;
    }
  });

  return (
    <AppDialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth sx={{ '& .MuiButton-root': { borderRadius: '6px' } }}>
      <DialogTitle sx={{ pr: 6 }}>
        {t('dataInput.outsourcing.title')}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('dataInput.outsourcing.period', { year })}
        </Typography>
        <IconButton
          onClick={onClose}
          disabled={saving}
          aria-label={t('common.close')}
          sx={{ position: 'absolute', right: 12, top: 12 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {errors.root?.server?.message && (
          <Alert severity="error" sx={{ mb: 2 }}>{errors.root.server.message}</Alert>
        )}
        <Grid
          container
          spacing={2.5}
          component="form"
          id="outsourcing-form"
          onSubmit={submit}
        >
          <Grid item xs={12}>
            <Controller
              name="amount_jpy"
              control={control}
              rules={{
                required: t('common.fieldRequired'),
                validate: (value) => (
                  isValidOutsourcingAmount(value)
                  || t('dataInput.outsourcing.amountInvalid')
                ),
              }}
              render={({ field }) => (
                <LabeledField
                  label={t('dataInput.outsourcing.amount')}
                  htmlFor="amount_jpy"
                  required
                  error={errors.amount_jpy?.message}
                >
                  <OutlinedInput
                    {...field}
                    id="amount_jpy"
                    size="small"
                    type="number"
                    error={Boolean(errors.amount_jpy)}
                    startAdornment={<InputAdornment position="start">¥</InputAdornment>}
                    inputProps={{ min: 0, step: 1, inputMode: 'numeric' }}
                  />
                </LabeledField>
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              name="note"
              control={control}
              rules={{ maxLength: { value: 500, message: t('dataInput.outsourcing.noteTooLong') } }}
              render={({ field }) => (
                <LabeledField
                  label={t('dataInput.outsourcing.note')}
                  htmlFor="outsourcing_note"
                  error={errors.note?.message}
                >
                  <OutlinedInput
                    {...field}
                    id="outsourcing_note"
                    size="small"
                    error={Boolean(errors.note)}
                    multiline
                    minRows={3}
                    placeholder={t('dataInput.outsourcing.notePlaceholder')}
                  />
                </LabeledField>
              )}
            />
            <Typography
              variant="caption"
              color={noteLength > 500 ? 'error.main' : 'text.disabled'}
              sx={{ display: 'block', mt: 0.75, textAlign: 'right' }}
            >
              {noteLength}/500
            </Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5 }}>
        <Button onClick={onClose} disabled={saving}>{t('common.cancel')}</Button>
        <Button
          type="submit"
          form="outsourcing-form"
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress color="inherit" size={15} /> : undefined}
        >
          {t(isExisting
            ? (saving ? 'common.updating' : 'common.update')
            : (saving ? 'common.creating' : 'common.create'))}
        </Button>
      </DialogActions>
    </AppDialog>
  );
}
